import { useState } from "react"
import type { ComponentProps, SubmitEvent } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Input from "@/v2-ui/input"
import Label from "@/v2-ui/label"
import Separator from "@/v2-ui/separator"
import TenantDefaultsFields from "@/v2-backoffice-client/tenant/update/TenantDefaultsFields"
import {
  type TenantDefaultsValues,
  useTenantDefaultsValues,
} from "@/v2-backoffice-client/tenant/update/tenant.update.hooks"
import type { TenantFragment } from "@/v2-backoffice-client/tenant/__types__/tenant.fragment"

export type TenantUpdateFormData = {
  Name: string
  // The member-defaults write, or null when there is nothing to write
  // (nothing changed, or the stored defaults haven't loaded yet).
  defaults: TenantDefaultsValues | null
}

export type TenantUpdateFormProps = Omit<ComponentProps<"form">, "onSubmit"> & {
  tenant: TenantFragment
  onSubmit(data: TenantUpdateFormData, e: SubmitEvent<HTMLFormElement>): void
}

// Everything editable about a team in one place — its name, and what a
// member joining it starts with. The member defaults live here rather than
// behind a toolbar button of their own, the same way a user's active
// project lives in the user update dialog.
//
// Mounted inside Dialog.Content, so every opening is a fresh mount — the
// name initializer below is the per-open seeding, and a cancelled edit is
// discarded with the unmount.
function TenantUpdateForm(props: TenantUpdateFormProps) {
  const { tenant, onSubmit, className, ...formProps } = props

  const [name, setName] = useState(tenant.Name)
  const defaults = useTenantDefaultsValues(tenant.Id)

  return (
    <form
      className={cn("space-y-4 py-4", className)}
      onSubmit={(e) => {
        e.preventDefault()
        onSubmit({ Name: name, defaults: defaults.values }, e)
      }}
      {...formProps}
    >
      <div className="space-y-2">
        <Label htmlFor="editTenantName">Team Name</Label>
        <Input
          id="editTenantName"
          type="text"
          required
          autoFocus
          value={name}
          onChange={(e) => {
            setName(e.target.value)
          }}
        />
      </div>

      <Separator />

      <ErrorMessage error={defaults.queryError} />
      <TenantDefaultsFields context={defaults} />
    </form>
  )
}

export default TenantUpdateForm
